import {
  APP_TRACK_QUERY_EVENTS,
  type AppTrack,
} from "../../models/AppTrack";
import { getTruckPlateColorLabel } from "../../models/Truck";
import { TruckLength, TruckType } from "../../utils/truck-utils";
import { formatDate } from "../../utils/common";

export { APP_TRACK_QUERY_EVENTS };

/** 事件中文名映射。 */
export const APP_TRACK_EVENT_LABELS: Record<string, string> = {
  open_app: "打开 App",
  view_cargo_detail: "查看货源详情",
  query_cargo_list_by_origin: "按出发地查询货源",
};

/** 详情弹窗中已在上方展示、无需重复的字段。 */
const HIDDEN_PROPERTY_KEYS = new Set([
  "cargo_image_url",
  "cargoImageUrl",
  "image_url",
  "imageUrl",
  "image",
  "cargo_id",
  "cargoId",
  "cargo_name",
  "cargoName",
  "user_id",
  "userId",
  "event_time",
  "eventTime",
]);

/** 属性字段中文标签。 */
const PROPERTY_KEY_LABELS: Record<string, string> = {
  event_id: "事件 ID",
  eventId: "事件 ID",
  session_id: "会话 ID",
  sessionId: "会话 ID",
  query_id: "查询 ID",
  queryId: "查询 ID",
  user_role: "用户角色",
  userRole: "用户角色",
  platform: "设备平台",
  app_version: "App 版本",
  appVersion: "App 版本",
  network_type: "网络类型",
  networkType: "网络类型",
  page_source: "页面来源",
  pageSource: "页面来源",
  entry_source: "进入来源",
  entrySource: "进入来源",
  list_position: "列表位置",
  listPosition: "列表位置",
  list_page_no: "列表页码",
  listPageNo: "列表页码",
  origin_city_name: "出发城市",
  originCityName: "出发城市",
  destination_city_name: "目的城市",
  destinationCityName: "目的城市",
  cargo_status: "货源状态",
  cargoStatus: "货源状态",
  cargo_price_type: "计价方式",
  cargoPriceType: "计价方式",
  origin: "出发地",
  origin_city: "出发城市",
  originCity: "出发城市",
  from_city: "出发城市",
  fromCity: "出发城市",
  city: "城市",
  province: "省份",
  district: "区县",
  keyword: "关键词",
  query: "查询词",
  page: "页码",
  page_size: "每页数量",
  pageSize: "每页数量",
  limit: "数量限制",
};

/** 用户角色语义。 */
const USER_ROLE_LABELS: Record<string, string> = {
  driver: "司机",
  owner: "货主",
  shipper: "货主",
  cargo_owner: "货主",
  admin: "管理员",
};

/** 平台语义。 */
const PLATFORM_LABELS: Record<string, string> = {
  ios: "iOS",
  android: "Android",
  web: "Web",
  h5: "H5",
  mini_program: "小程序",
  miniprogram: "小程序",
};

/** 网络类型语义。 */
const NETWORK_LABELS: Record<string, string> = {
  wifi: "Wi-Fi",
  "4g": "4G",
  "5g": "5G",
  "3g": "3G",
  cellular: "移动网络",
  none: "无网络",
  unknown: "未知",
};

/** 页面/进入来源语义。 */
const SOURCE_LABELS: Record<string, string> = {
  home_cargo_list: "首页货源列表",
  cargo_list: "货源列表",
  cargo_detail: "货源详情",
  search: "搜索",
  push: "推送",
  share: "分享",
  map: "地图",
  favorite: "收藏",
  history: "历史记录",
};

/** 货源状态语义。 */
const CARGO_STATUS_LABELS: Record<string, string> = {
  "1": "上架中",
  "2": "主动下架",
  "3": "超时下架",
};

/** 计价方式语义。 */
const CARGO_PRICE_TYPE_LABELS: Record<string, string> = {
  unit: "按吨计价",
  unit_price: "按吨计价",
  ton: "按吨计价",
  single: "按车计价",
  single_price: "按车计价",
  truck: "按车计价",
  "0": "按吨计价",
  "1": "按车计价",
};

/**
 * 司机车辆信息。
 */
export type AppTrackTruckInfo = {
  id: string;
  plate: string;
  plateColor: string;
  truckType: string;
  truckLength: string;
  phone: string;
};

/**
 * 获取事件中文展示名。
 */
export const getAppTrackEventLabel = (event: string | null | undefined): string => {
  if (!event) {
    return "-";
  }
  return APP_TRACK_EVENT_LABELS[event] || event;
};

/**
 * 从任意值中提取非空字符串。
 */
const pickString = (value: unknown): string => {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return "";
};

/**
 * 从 properties 中递归提取货源 ID。
 */
export const extractCargoIdFromProperties = (
  properties: AppTrack["properties"]
): string => {
  if (!properties) {
    return "";
  }

  const directKeys = [
    "cargo_id",
    "cargoId",
    "cargoID",
    "valid_cargo_id",
    "validCargoId",
  ];
  for (const key of directKeys) {
    const value = pickString(properties[key]);
    if (value) {
      return value;
    }
  }

  const cargo = properties.cargo;
  if (typeof cargo === "string" || typeof cargo === "number") {
    return pickString(cargo);
  }
  if (cargo && typeof cargo === "object" && !Array.isArray(cargo)) {
    const nested = cargo as Record<string, unknown>;
    const nestedId = pickString(nested.id ?? nested._id ?? nested.cargo_id ?? nested.cargoId);
    if (nestedId) {
      return nestedId;
    }
  }

  return "";
};

/**
 * 从 properties 中提取出发地/城市等查询条件摘要。
 */
export const extractOriginSummary = (
  properties: AppTrack["properties"]
): string => {
  if (!properties) {
    return "";
  }

  const originKeys = [
    "origin",
    "origin_city",
    "originCity",
    "origin_city_name",
    "originCityName",
    "destination_city_name",
    "destinationCityName",
    "from_city",
    "fromCity",
    "city",
    "province",
    "district",
    "keyword",
    "query",
  ];
  const parts: string[] = [];
  for (const key of originKeys) {
    const value = pickString(properties[key]);
    if (value && !parts.includes(value)) {
      parts.push(value);
    }
  }

  const from = properties.from;
  if (from && typeof from === "object" && !Array.isArray(from)) {
    const nested = from as Record<string, unknown>;
    for (const key of ["province", "city", "district", "address"]) {
      const value = pickString(nested[key]);
      if (value && !parts.includes(value)) {
        parts.push(value);
      }
    }
  }

  return parts.join(" · ");
};

/**
 * 生成列表用的事件摘要（不展示原始 JSON）。
 */
export const buildAppTrackSummary = (record: AppTrack | null | undefined): string => {
  if (!record) {
    return "-";
  }
  const event = (record.event || "").trim().toLowerCase();
  const properties = record.properties;
  const cargoId = extractCargoIdFromProperties(properties);
  const origin = extractOriginSummary(properties);

  if (event === "open_app") {
    return "打开应用";
  }
  if (event === "view_cargo_detail") {
    return cargoId ? `查看货源 ${cargoId}` : "查看货源详情";
  }
  if (event === "query_cargo_list_by_origin") {
    return origin ? `查询：${origin}` : "按出发地查询货源列表";
  }
  if (cargoId) {
    return `货源 ${cargoId}`;
  }
  if (origin) {
    return origin;
  }
  return getAppTrackEventLabel(record.event);
};

/**
 * 格式化时间戳 / 时间字符串。
 */
const formatPropertyTime = (value: unknown): string => {
  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    return formatDate(new Date(ms).toISOString());
  }
  if (typeof value === "string" && value.trim()) {
    const asNumber = Number(value);
    if (Number.isFinite(asNumber) && /^\d+$/.test(value.trim())) {
      const ms = asNumber < 1e12 ? asNumber * 1000 : asNumber;
      return formatDate(new Date(ms).toISOString());
    }
    return formatDate(value);
  }
  return "";
};

/**
 * 将单个属性值转为语义化文案。
 */
const formatPropertyValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const normalizedKey = key.trim();
  const textValue =
    typeof value === "string" || typeof value === "number" || typeof value === "boolean"
      ? String(value)
      : "";

  if (
    normalizedKey === "event_time"
    || normalizedKey === "eventTime"
    || normalizedKey.endsWith("_time")
    || normalizedKey.endsWith("Time")
  ) {
    const formatted = formatPropertyTime(value);
    if (formatted) {
      return formatted;
    }
  }

  if (normalizedKey === "user_role" || normalizedKey === "userRole") {
    const lower = textValue.toLowerCase();
    return USER_ROLE_LABELS[lower] || textValue;
  }

  if (normalizedKey === "platform") {
    const lower = textValue.toLowerCase();
    return PLATFORM_LABELS[lower] || textValue;
  }

  if (normalizedKey === "network_type" || normalizedKey === "networkType") {
    const lower = textValue.toLowerCase();
    return NETWORK_LABELS[lower] || textValue;
  }

  if (
    normalizedKey === "page_source"
    || normalizedKey === "pageSource"
    || normalizedKey === "entry_source"
    || normalizedKey === "entrySource"
  ) {
    const lower = textValue.toLowerCase();
    return SOURCE_LABELS[lower] || textValue;
  }

  if (normalizedKey === "cargo_status" || normalizedKey === "cargoStatus") {
    return CARGO_STATUS_LABELS[textValue] || textValue;
  }

  if (normalizedKey === "cargo_price_type" || normalizedKey === "cargoPriceType") {
    const lower = textValue.toLowerCase();
    return CARGO_PRICE_TYPE_LABELS[lower] || CARGO_PRICE_TYPE_LABELS[textValue] || textValue;
  }

  if (normalizedKey === "list_position" || normalizedKey === "listPosition") {
    const position = Number(value);
    if (Number.isFinite(position)) {
      return `第 ${position} 位`;
    }
  }

  if (
    normalizedKey === "list_page_no"
    || normalizedKey === "listPageNo"
    || normalizedKey === "page"
  ) {
    const page = Number(value);
    if (Number.isFinite(page)) {
      return `第 ${page + 1} 页`;
    }
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

/**
 * 将 properties 转为语义化键值对（详情弹窗用）。
 * 会隐藏货源图片链接及已在上方展示的冗余字段。
 */
export const formatPropertiesAsEntries = (
  properties: AppTrack["properties"]
): Array<{ key: string; value: string }> => {
  if (!properties || Object.keys(properties).length === 0) {
    return [];
  }

  return Object.entries(properties)
    .filter(([key, value]) => {
      if (HIDDEN_PROPERTY_KEYS.has(key)) {
        return false;
      }
      // 隐藏各类图片链接字段
      if (/image|img|avatar|photo/i.test(key) && typeof value === "string" && /^https?:\/\//i.test(value)) {
        return false;
      }
      return true;
    })
    .map(([key, value]) => ({
      key: PROPERTY_KEY_LABELS[key] || key,
      value: formatPropertyValue(key, value),
    }));
};

/**
 * 解析单条车辆记录。
 */
const parseTruckInfo = (raw: unknown): AppTrackTruckInfo | null => {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const truck = raw as Record<string, unknown>;
  const plate = pickString(truck.plate ?? truck.licensePlate ?? truck.license_plate);
  const typeText = pickString(truck.type);
  const truckTypeCode = typeof truck.truck_type === "number"
    ? truck.truck_type
    : typeof truck.truckType === "number"
      ? truck.truckType
      : Number(truck.truck_type ?? truck.truckType);
  const truckLengthCode = typeof truck.truck_length === "number"
    ? truck.truck_length
    : typeof truck.truckLength === "number"
      ? truck.truckLength
      : Number(truck.truck_length ?? truck.truckLength);
  const plateColorCode = typeof truck.plate_color === "number"
    ? truck.plate_color
    : typeof truck.plateColor === "number"
      ? truck.plateColor
      : Number(truck.plate_color ?? truck.plateColor);
  const phone = pickString(truck.phone);
  const id = pickString(truck.id ?? truck._id);

  if (!id && !plate && !typeText && !Number.isFinite(truckTypeCode)) {
    return null;
  }

  return {
    id,
    plate: plate || "-",
    plateColor: Number.isFinite(plateColorCode)
      ? getTruckPlateColorLabel(plateColorCode)
      : "-",
    truckType: Number.isFinite(truckTypeCode)
      ? TruckType(truckTypeCode)
      : typeText || "-",
    truckLength: Number.isFinite(truckLengthCode)
      ? TruckLength(truckLengthCode)
      : "-",
    phone,
  };
};

/**
 * 从司机详情响应中解析车辆列表。
 */
export const parseDriverTrucks = (raw: unknown): AppTrackTruckInfo[] => {
  let payload: unknown = raw;
  if (typeof raw === "object" && raw !== null && "data" in raw) {
    payload = (raw as { data: unknown }).data;
  }
  if (!payload || typeof payload !== "object") {
    return [];
  }
  const record = payload as Record<string, unknown>;
  const trucksRaw = record.trucks ?? record.Vehicles ?? record.vehicles;
  if (!Array.isArray(trucksRaw)) {
    return [];
  }
  return trucksRaw
    .map((item) => parseTruckInfo(item))
    .filter((item): item is AppTrackTruckInfo => item !== null);
};

/**
 * 判断埋点用户是否为司机角色。
 */
export const isDriverRoleFromProperties = (
  properties: AppTrack["properties"]
): boolean => {
  if (!properties) {
    return false;
  }
  const role = pickString(properties.user_role ?? properties.userRole).toLowerCase();
  return role === "driver" || role === "司机";
};

/**
 * 解析用户信息（兼容 users/many、fetchUserInfo）。
 */
export type AppTrackUserInfo = {
  id: string;
  name: string;
  realName: string;
  phone: string;
  avatar: string;
};

/**
 * 从接口响应解析用户基本信息。
 */
export const parseAppTrackUserInfo = (raw: unknown): AppTrackUserInfo | null => {
  let candidate: unknown = raw;
  if (Array.isArray(raw)) {
    candidate = raw[0];
  } else if (raw && typeof raw === "object") {
    const record = raw as Record<string, unknown>;
    if (Array.isArray(record.data)) {
      candidate = record.data[0];
    } else if (record.data && typeof record.data === "object") {
      candidate = record.data;
    } else if (Array.isArray(record.users)) {
      candidate = record.users[0];
    }
  }

  if (!candidate || typeof candidate !== "object") {
    return null;
  }

  const user = candidate as Record<string, unknown>;
  const id = pickString(user.id ?? user._id);
  const name =
    pickString(user.name)
    || pickString(user.nickname)
    || pickString(user.username)
    || pickString(user.fullName);
  const realName = pickString(user.real_name ?? user.realName ?? user.realname);
  const phone = pickString(user.phone ?? user.mobile ?? user.phoneNumber);
  const avatar = pickString(user.avatar ?? user.photo ?? user.headimgurl);

  if (!id && !name && !realName && !phone) {
    return null;
  }

  return {
    id,
    name: name || realName || "-",
    realName,
    phone,
    avatar,
  };
};

/**
 * 从列表/数组响应中解析多条用户信息。
 */
export const parseAppTrackUserInfoList = (raw: unknown): AppTrackUserInfo[] => {
  let list: unknown[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (raw && typeof raw === "object") {
    const record = raw as Record<string, unknown>;
    if (Array.isArray(record.data)) {
      list = record.data;
    } else if (Array.isArray(record.users)) {
      list = record.users;
    } else {
      const single = parseAppTrackUserInfo(raw);
      return single ? [single] : [];
    }
  }

  const result: AppTrackUserInfo[] = [];
  for (const item of list) {
    const parsed = parseAppTrackUserInfo(item);
    if (parsed?.id) {
      result.push(parsed);
    }
  }
  return result;
};

/**
 * 构造跳转到货源行为数据页的路径（按用户 ID + 默认事件过滤）。
 */
export const buildAppTrackPathByUserId = (userId: string): string => {
  const filter = encodeURIComponent(
    JSON.stringify({ user_id: userId, event: "view_cargo_detail" })
  );
  const displayedFilters = encodeURIComponent(
    JSON.stringify({ user_id: true, event: true })
  );
  return `/appTrack?filter=${filter}&displayedFilters=${displayedFilters}`;
};
